import React, { useMemo } from 'react'
import dateFormat from 'dateformat'
import { IconType } from '../../../atoms/Icon/Icon'
import { Colour } from '../../../../types/theme'
import { RootState } from '../../../../store'
import VerticalGroup from '../../../atoms/VerticalGroup/VerticalGroup'

import HorizontalGroup from '../../../atoms/HorizontalGroup/HorizontalGroup'

import MetricCardIconBox from '../../../organisms/MetricCard/MetricCardIconBox'

import BrandedButton from '../../../organisms/Branding/BrandedButton/BrandedButton'
import BrandedText from '../../../organisms/Branding/BrandedText/BrandedText'
import SecondaryText from '../../../atoms/Text/SecondaryText'

interface Props {
  event: RootState['event']['details']
  game: RootState['game']
  device: RootState['device']
  onEnterEventButtonPressed: () => void
}

interface EventStatus {
  title: string
  message?: string
  colour: Colour
  enableButton: boolean
  icon: IconType
}

const WaitingRoomEventInfoCard: React.FC<Props> = ({
  event,
  game,
  device,
  onEnterEventButtonPressed,
}) => {
  const { gameStarted, deviceStatusChecked, eventClosed } = useMemo(() => {
    return {
      gameStarted: game && game.currentRound > 0,
      deviceStatusChecked:
        device && device.registered
          ? device.deviceStatusChecked
          : !!device?.error,
      eventClosed: event?.eventClosed,
    }
  }, [device, event?.eventClosed, game])

  const {
    state: gameState,
    roundType,
    nextRoundStartTime,
    gameStartTime,
  } = game || {}

  const eventStatus: EventStatus = React.useMemo(() => {
    if (eventClosed) {
      return {
        title: 'This event has now closed',
        colour: 'error',
        enableButton: false,
        icon: 'locked',
      }
    }

    if (!deviceStatusChecked) {
      return {
        title: 'Checking status of the event...',
        colour: 'primary',
        enableButton: false,
        icon: 'notStarted',
      }
    }

    if (!gameStarted) {
      if (roundType === 'scheduled' && gameStartTime) {
        return {
          title: 'This event has not yet commenced',
          message: `This event will commence at: ${dateFormat(
            gameStartTime,
            'hh:MMtt',
          )} on ${dateFormat(gameStartTime, 'dd mmmm yyyy')}`,
          colour: 'primary',
          enableButton: false,
          icon: 'notStarted',
        }
      }
      return {
        title: 'This event has not yet commenced',
        colour: 'purple',
        enableButton: false,
        icon: 'checking',
      }
    } else if (gameState === 'locked') {
      if (roundType === 'scheduled' && nextRoundStartTime) {
        return {
          title: 'This event will resume shortly',
          message: `This event will resume at: ${dateFormat(
            nextRoundStartTime,
            'hh:MMtt',
          )} on ${dateFormat(nextRoundStartTime, 'dd mmmm yyyy')}`,
          colour: 'orange',
          enableButton: false,
          icon: 'willResume',
        }
      }
      return {
        title: 'This event will resume shortly',
        colour: 'orange',
        enableButton: false,
        icon: 'willResume',
      }
    }
    return {
      title: "Let's Go!",
      message: 'This event has commenced, use the button below to enter',
      enableButton: true,
      icon: 'ready',
      colour: 'success',
    }
  }, [
    deviceStatusChecked,
    eventClosed,
    gameStartTime,
    gameStarted,
    gameState,
    nextRoundStartTime,
    roundType,
  ])

  return (
    <VerticalGroup
      className="pb-4  tablet:h-64 tablet:justify-end  tablet:pb-0 desktop:pb-0   "
      between
      full
      fullWidth
    >
      <VerticalGroup
        gap={4}
        className="  h-44 pt-2 tablet:ml-1.5 tablet:border-l-2  tablet:pt-0 tablet:pl-7 monitor:gap-4  "
      >
        <HorizontalGroup between fullWidth verticalCenter>
          <BrandedText colourField="mainTextColour" size="lg" weight="bold">
            Event Status
          </BrandedText>
        </HorizontalGroup>
        <VerticalGroup gap={4} className=" " verticalCenter fullWidth>
          <HorizontalGroup className="laptop:h-20 " gap={2}>
            <MetricCardIconBox
              iconSize={14}
              useLightShades
              size="large"
              iconType={eventStatus.icon}
              iconColour={eventStatus.colour}
              backgroundColour={eventStatus.colour}
            ></MetricCardIconBox>
            <VerticalGroup className="bg-succes" verticalCenter>
              <BrandedText
                colourField="mainTextColour"
                family="secondary"
                weight="bold"
              >
                {eventStatus.title}
              </BrandedText>
              {eventStatus.message ? (
                <BrandedText
                  colourField="mainTextColour"
                  family="secondary"
                  size="sm"
                >
                  {eventStatus.message}
                </BrandedText>
              ) : (
                <BrandedText colourField="mainTextColour" family="secondary">
                  &nbsp;
                </BrandedText>
              )}
            </VerticalGroup>
          </HorizontalGroup>
          <VerticalGroup full>
            <BrandedButton
              size="xl"
              disabled={!eventStatus.enableButton}
              onClick={
                eventStatus.enableButton ? onEnterEventButtonPressed : undefined
              }
            >
              <SecondaryText size="sm">Enter Event</SecondaryText>
            </BrandedButton>
          </VerticalGroup>
        </VerticalGroup>
      </VerticalGroup>
    </VerticalGroup>
  )
}

export default WaitingRoomEventInfoCard
